import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import QRCode from 'qrcode';
import './CheckoutPage.css';

type PaymentStatus = 'waiting' | 'completed' | 'failed' | 'expired';

interface OrderData {
  orderId: string;
  status: PaymentStatus;
  usdtAmount: number;
  currency: string;
  cryptoAmount: number;
  approximateTotalUsdt: number;
  serviceFeeUsdt: number;
  transferFeeUsdt: number;
  address: string;
  expiresAt: string | null;
  description?: string | null;
  comebackUrl?: string | null;
  timeRemaining: { hours: number; minutes: number; total: number };
}

export function CheckoutPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const [order, setOrder] = useState<OrderData | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [isDevMode, setIsDevMode] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [walletPaying, setWalletPaying] = useState(false);
  const [walletMsg, setWalletMsg] = useState<{ text: string; type: 'error' | 'info' | 'success' } | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const notifiedRef = useRef(false);

  const fetchOrder = useCallback(async () => {
    if (!orderId) return;
    try {
      const res = await fetch(`/api/orders/${orderId}`);
      if (!res.ok) return;
      const data: OrderData = await res.json();
      setOrder(data);
      if (data.timeRemaining?.total > 0) {
        setTimeLeft(Math.floor(data.timeRemaining.total / 1000));
      }

      // Notify parent frame on status change
      if (!notifiedRef.current && data.status !== 'waiting') {
        notifiedRef.current = true;
        const msg = {
          type: 'plzpay:status',
          orderId: data.orderId,
          status: data.status,
          txId: null,
        };
        try { window.parent.postMessage(msg, '*'); } catch {}

        if (data.status === 'completed') {
          stopPolling();
        }
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  useEffect(() => {
    fetchOrder();
    pollRef.current = setInterval(fetchOrder, 5000);
    return () => stopPolling();
  }, [fetchOrder, stopPolling]);

  // Check if dev mode is enabled
  useEffect(() => {
    fetch('/api/dev/info').then(r => r.ok && r.json()).then(d => {
      if (d?.enabled) setIsDevMode(true);
    }).catch(() => {});
  }, []);

  // Countdown timer
  useEffect(() => {
    if (timeLeft <= 0) return;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [timeLeft > 0]);

  // Generate QR code when address is available
  useEffect(() => {
    if (!order?.address) return;
    QRCode.toDataURL(order.address, {
      width: 200,
      margin: 2,
      color: { dark: '#000000', light: '#ffffff' },
    }).then(setQrDataUrl).catch(() => {});
  }, [order?.address]);

  const handleCopy = useCallback(() => {
    if (!order?.address) return;
    navigator.clipboard.writeText(order.address).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [order?.address]);

  const handleSimulate = useCallback(async () => {
    if (!orderId || simulating) return;
    setSimulating(true);
    try {
      const res = await fetch(`/api/dev/simulate/payment/${orderId}`, { method: 'POST' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.error('[DEV] Simulate failed:', err);
      } else {
        // Poll immediately to pick up status change
        await fetchOrder();
      }
    } catch (e) {
      console.error('[DEV] Simulate error:', e);
    }
    setSimulating(false);
  }, [orderId, simulating, fetchOrder]);

  const handleClose = useCallback(() => {
    try {
      window.parent.postMessage({ type: 'plzpay:close', orderId }, '*');
    } catch {}
  }, [orderId]);

  const handleReturn = useCallback(() => {
    if (order?.comebackUrl) {
      window.top ? (window.top.location.href = order.comebackUrl) : (window.location.href = order.comebackUrl);
    }
  }, [order?.comebackUrl]);

  // Detect which TRON wallet is injected (TronLink, OKX, Bybit, etc.)
  const getTronWalletName = (): string => {
    const win = window as any;
    if (win.okxwallet?.tronLink) return 'OKX Wallet';
    if (win.bybitWallet?.tronWeb) return 'Bybit Wallet';
    if (win.tronLink) return 'TronLink';
    if (win.tronWeb) return 'TRON Wallet';
    return 'TRON Wallet';
  };

  // TRON Wallet — supports TronLink, OKX Wallet, Bybit Wallet, Ledger via TronLink
  const handlePayWithTronWallet = useCallback(async () => {
    if (!order || walletPaying) return;
    const win = window as any;

    const tronLink = win.okxwallet?.tronLink ?? win.tronLink;
    const tronWeb = win.tronWeb;

    if (!tronLink && !tronWeb) {
      setWalletMsg({ text: 'TRON wallet not found. Please install TronLink or OKX Wallet.', type: 'info' });
      return;
    }

    setWalletPaying(true);
    setWalletMsg(null);

    try {
      // Request account access
      if (tronLink?.request) {
        const res = await tronLink.request({ method: 'tron_requestAccounts' });
        if (res?.code === 4001) return; // user rejected
      }

      if (!tronWeb?.ready) {
        setWalletMsg({ text: 'Wallet is locked. Please unlock it and try again.', type: 'error' });
        return;
      }

      const currency = order.currency.toUpperCase();

      if (currency === 'TRX') {
        // Send TRX directly
        const amountSun = Math.round(order.cryptoAmount * 1_000_000);
        await tronWeb.trx.sendTransaction(order.address, amountSun);
      } else {
        // Send TRC-20 token (USDT or other)
        const USDT_TRC20 = 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t';
        const tokenDecimals = 6;
        const amountUnits = Math.round(order.cryptoAmount * Math.pow(10, tokenDecimals));
        const contract = await tronWeb.contract().at(USDT_TRC20);
        await contract.transfer(order.address, amountUnits).send({
          feeLimit: 100_000_000,
          shouldPollResponse: false,
        });
      }

      setWalletMsg({ text: 'Transaction sent! Waiting for confirmation…', type: 'success' });
    } catch (err: any) {
      const msg: string = err?.message ?? '';
      if (err?.code === 4001 || msg === 'Confirmation declined by user') return;
      if (msg.toLowerCase().includes('balance')) {
        setWalletMsg({ text: 'Insufficient balance in your wallet.', type: 'error' });
      } else if (msg.toLowerCase().includes('bandwidth') || msg.toLowerCase().includes('energy')) {
        setWalletMsg({ text: 'Not enough TRX for network fees. Add at least 10 TRX to your wallet.', type: 'error' });
      } else {
        setWalletMsg({ text: msg || 'Transaction failed. Please try again.', type: 'error' });
      }
    } finally {
      setWalletPaying(false);
    }
  }, [order, walletPaying]);


  const formatCryptoAmount = (amount: number, currency: string): string => {
    const decimalsMap: Record<string, number> = {
      USDT: 2, TRX: 2, ETH: 6, BTC: 8,
    };
    const decimals = decimalsMap[currency.toUpperCase()] ?? 6;
    return parseFloat(amount.toFixed(decimals)).toString();
  };

  const getNetworkHint = (currency: string): string => {
    const map: Record<string, string> = {
      USDT: 'TRON (TRC20)',
      TRX: 'TRON (TRC20)',
      ETH: 'Ethereum (ERC20)',
      BTC: 'Bitcoin (BTC)',
    };
    return map[currency.toUpperCase()] ?? currency;
  };

  const isNonUsdtCrypto = (currency: string) =>
    !['USDT', 'TRX'].includes(currency.toUpperCase());

  const formatTime = (secs: number) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="checkout-page">
        <div className="checkout-loading">
          <div className="checkout-spinner" />
          <p>Loading payment…</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="checkout-page">
        <div className="checkout-error">
          <div className="checkout-error-icon">⚠️</div>
          <h2>Order not found</h2>
          <p>This payment link may have expired or is invalid.</p>
        </div>
      </div>
    );
  }

  const isCompleted = order.status === 'completed';
  const isFailed = order.status === 'failed' || order.status === 'expired';

  return (
    <div className="checkout-page">
      <div className="checkout-card">
        {/* Header */}
        <div className="checkout-header">
          <div className="checkout-brand">
            <span className="checkout-brand-logo">₮</span>
            <span className="checkout-brand-name">Plz Pay Me</span>
          </div>
          <button className="checkout-close-btn" onClick={handleClose} title="Close">×</button>
        </div>

        {/* Status */}
        {isCompleted ? (
          <div className="checkout-success">
            <div className="checkout-success-icon">✓</div>
            <h2>Payment received!</h2>
            <p>Your payment of <strong>{formatCryptoAmount(order.cryptoAmount, order.currency)} {order.currency}</strong> has been confirmed.</p>
            {order.comebackUrl && (
              <button className="checkout-btn checkout-btn-primary" onClick={handleReturn}>
                Return to merchant →
              </button>
            )}
          </div>
        ) : isFailed ? (
          <div className="checkout-failed">
            <div className="checkout-failed-icon">✗</div>
            <h2>{order.status === 'expired' ? 'Payment expired' : 'Payment failed'}</h2>
            <p>This payment order is no longer active.</p>
            {order.comebackUrl && (
              <button className="checkout-btn checkout-btn-secondary" onClick={handleReturn}>
                ← Back to merchant
              </button>
            )}
          </div>
        ) : (
          /* Waiting */
          <>
            <div className="checkout-amount-section">
              {order.description && (
                <p className="checkout-description">{order.description}</p>
              )}

              {/* Fee breakdown */}
              <div className="checkout-breakdown">
                <div className="checkout-breakdown-row">
                  <span className="checkout-breakdown-label">Amount</span>
                  <span className="checkout-breakdown-value">{order.usdtAmount.toFixed(2)} USDT</span>
                </div>
                {order.serviceFeeUsdt > 0 && (
                  <div className="checkout-breakdown-row checkout-breakdown-fee">
                    <span className="checkout-breakdown-label">Service fee</span>
                    <span className="checkout-breakdown-value">+{order.serviceFeeUsdt.toFixed(2)} USDT</span>
                  </div>
                )}
                {order.transferFeeUsdt > 0 && (
                  <div className="checkout-breakdown-row checkout-breakdown-fee">
                    <span className="checkout-breakdown-label">Network fee</span>
                    <span className="checkout-breakdown-value">+{order.transferFeeUsdt.toFixed(2)} USDT</span>
                  </div>
                )}
                <div className="checkout-breakdown-divider" />
                <div className="checkout-breakdown-row checkout-breakdown-total">
                  <span className="checkout-breakdown-label">Total to send</span>
                  <div className="checkout-breakdown-total-value">
                    <span className="checkout-amount-value">{formatCryptoAmount(order.cryptoAmount, order.currency)}</span>
                    <span className="checkout-amount-currency">{order.currency}</span>
                  </div>
                </div>
                {isNonUsdtCrypto(order.currency) && order.approximateTotalUsdt > 0 && (
                  <p className="checkout-usdt-equiv">≈ {order.approximateTotalUsdt.toFixed(2)} USDT</p>
                )}
              </div>
            </div>

            {/* TRON Wallet pay button — works with TronLink, OKX Wallet, Bybit Wallet, Ledger */}
            {['USDT', 'TRX'].includes(order.currency.toUpperCase()) && (
              <div className="checkout-wallet-section">
                <div className="checkout-wallet-divider"><span>Pay with wallet</span></div>
                <button
                  className="checkout-wallet-btn tronlink checkout-wallet-btn--full"
                  onClick={handlePayWithTronWallet}
                  disabled={walletPaying}
                  title="Pay with TronLink, OKX Wallet, Bybit Wallet or Ledger (TRON network)"
                >
                  {walletPaying ? (
                    <span className="checkout-wallet-spinner" />
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 32 32" fill="none">
                      <circle cx="16" cy="16" r="16" fill="#E8003D"/>
                      <path d="M8 10l8 2 8-2-8 12L8 10z" fill="white" opacity="0.9"/>
                      <path d="M16 12l-5 10 5-3 5 3L16 12z" fill="white"/>
                    </svg>
                  )}
                  {walletPaying ? 'Confirm in wallet…' : `Pay with ${getTronWalletName()}`}
                </button>
                <p className="checkout-wallet-hint">
                  Works with TronLink · OKX Wallet · Bybit Wallet · Ledger
                </p>
                {walletMsg && (
                  <p className={`checkout-wallet-msg checkout-wallet-msg--${walletMsg.type}`}>
                    {walletMsg.text}
                  </p>
                )}
                {!walletMsg && (
                  <p className="checkout-wallet-install-hint">
                    No wallet?{' '}
                    <a href="https://www.tronlink.org/" target="_blank" rel="noopener noreferrer">
                      Install TronLink
                    </a>{' '}or{' '}
                    <a href="https://www.okx.com/web3" target="_blank" rel="noopener noreferrer">
                      OKX Wallet
                    </a>
                  </p>
                )}
              </div>
            )}

            <div className="checkout-address-section">
              {qrDataUrl && (
                <div className="checkout-qr-wrap">
                  <img src={qrDataUrl} alt="QR code" className="checkout-qr" />
                  <p className="checkout-qr-hint">Scan with your crypto wallet</p>
                </div>
              )}
              <label className="checkout-label">Or copy address manually</label>
              <div className="checkout-address-box" onClick={handleCopy}>
                <span className="checkout-address-text">{order.address}</span>
                <button className="checkout-copy-btn">
                  {copied ? '✓ Copied' : 'Copy'}
                </button>
              </div>
              <p className="checkout-network-hint">Network: {getNetworkHint(order.currency)}</p>
            </div>

            {timeLeft > 0 && (
              <div className="checkout-timer">
                <span className="checkout-timer-label">Expires in</span>
                <span className="checkout-timer-value">{formatTime(timeLeft)}</span>
              </div>
            )}

            <div className="checkout-waiting-indicator">
              <div className="checkout-pulse" />
              <span>Waiting for payment…</span>
            </div>

            {isDevMode && (
              <div className="checkout-dev-bar">
                <span className="checkout-dev-label">DEV</span>
                <button
                  className="checkout-dev-btn"
                  onClick={handleSimulate}
                  disabled={simulating}
                >
                  {simulating ? 'Simulating…' : 'Simulate payment'}
                </button>
              </div>
            )}
          </>
        )}

        <div className="checkout-footer">
          <span>Secured by Plz Pay Me</span>
        </div>
      </div>
    </div>
  );
}

export default CheckoutPage;
